import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { getEmployeeListForSwitch, switchAccount } from '../../api/adminSwitch';
import { errorMessages } from '../../utils/methods';
import { setUserData, setUserMenu } from '../../actions/user';
import PasswordInputModal from './PasswordInputModal';
import SetPasswordModal from './SetPasswordModal';
import {
  SwitchModal,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  EmployeeCard,
  EmployeeAvatar,
  EmployeeInfo,
  PositionName,
  EmployeeName,
  SelectButton,
  ActiveButton,
  SectionDivider,
  SectionLabel,
} from './AdminSwitchStyled';

const ORIGINAL_USER_KEY = 'pila-original-user';
const EMPLOYEE_CACHE_KEY = 'pila-employee-cache';

// 원본 매니저 토큰 가져오기 (전환 상태면 원본, 아니면 현재)
const getManagerToken = currentToken => {
  try {
    const original = localStorage.getItem(ORIGINAL_USER_KEY);
    if (original) {
      const parsed = JSON.parse(original);
      return parsed.user?.token || currentToken;
    }
  } catch {
    // ignore
  }
  return currentToken;
};

const AdminSwitchModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const user = useSelector(state => state.user.user);
  const menu = useSelector(state => state.menu.menu);

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [setupPasswordModalOpen, setSetupPasswordModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const managerToken = getManagerToken(user?.token);
  const headBranchId = user?.headBranch?.headBranchId;
  const branchId = user?.branch?.branchId;

  const { data: employeeData } = useQuery({
    queryKey: ['adminSwitchEmployees', headBranchId, branchId],
    queryFn: () => getEmployeeListForSwitch(managerToken, headBranchId, branchId),
    enabled: open && !!managerToken && !!headBranchId,
    staleTime: 0,
  });

  const allList = (employeeData?.content || []).filter(e => e.classification === 'WORKING');
  const centerAccountList = allList.filter(e => e.isManager === 1);
  const representativeAndDefaultList = allList
    .filter(e => (e.isRepresentative === 'Y' || e.isDefaultAdmin === 'Y') && e.isManager !== 1)
    .sort((a, b) => {
      // 대표관리자 먼저, 기본관리자 뒤
      if (a.isRepresentative === 'Y' && b.isRepresentative !== 'Y') return -1;
      if (a.isRepresentative !== 'Y' && b.isRepresentative === 'Y') return 1;
      return 0;
    });

  // 직원을 직급별로 그룹핑 (sortOrder 순서 유지)
  const employeesByPosition = useMemo(() => {
    const posMap = {};
    const posList = [];
    allList.filter(e => e.isManager === 0 && e.isRepresentative !== 'Y' && e.isDefaultAdmin !== 'Y').forEach(emp => {
      const posName = emp.positionName || '미지정';
      if (!posMap[posName]) {
        posMap[posName] = { positionName: posName, sortOrder: emp.sortOrder ?? 99999, employees: [] };
        posList.push(posMap[posName]);
      }
      posMap[posName].employees.push(emp);
    });
    posList.sort((a, b) => a.sortOrder - b.sortOrder);
    return posList;
  }, [allList]);

  const handleSelectEmployee = async employee => {
    setSelectedEmployee(employee);
    // 기본관리자는 비밀번호 없이 바로 전환
    if (employee.isDefaultAdmin === 'Y') {
      try {
        const result = await switchAccount(managerToken, employee.userId, null, branchId);
        handleSwitchSuccess(result, employee);
      } catch (err) {
        toast.error(errorMessages(err));
      }
    } else {
      setPasswordModalOpen(true);
    }
  };

  const handleSwitchSuccess = (result, employee) => {
    // 원본 매니저 백업 (최초 전환 시에만)
    if (!localStorage.getItem(ORIGINAL_USER_KEY)) {
      localStorage.setItem(
        ORIGINAL_USER_KEY,
        JSON.stringify({ user: { ...user }, menu }),
      );
    }

    // 직원 목록 캐시 (전환 후에도 모달에서 사용)
    localStorage.setItem(EMPLOYEE_CACHE_KEY, JSON.stringify(allList));

    // 새 유저 데이터 구성
    const { userMenus, ...rest } = result;
    const newUser = {
      ...rest,
      userId: employee.userId,
      name: employee.name,
      headBranch: { headBranchId: result.headBranchId },
      branch: { branchId: result.branchId, branchName: user?.branch?.branchName, centerId: result.headBranchId },
    };

    dispatch(setUserData(newUser));
    dispatch(setUserMenu(userMenus || []));
    queryClient.clear();

    setPasswordModalOpen(false);
    onClose();

    window.location.replace('/member');
  };

  const handleSetPasswordSuccess = () => {
    setSetupPasswordModalOpen(false);
    queryClient.invalidateQueries({ queryKey: ['adminSwitchEmployees'] });
    setPasswordModalOpen(true);
  };

  const isCurrentUser = employee =>
    employee.userId === user?.userId || employee.username === user?.username ||
    (employee.isDefaultAdmin === 'Y' && user?.isDefaultAdmin === 'Y');

  const renderEmployeeCard = employee => (
    <EmployeeCard key={employee.userId}>
      <EmployeeAvatar>
        {employee.profileImage ? (
          <img src={employee.profileImage} alt={employee.name} />
        ) : (
          <img src="/images/chat-guest.svg" alt="default" />
        )}
      </EmployeeAvatar>
      <EmployeeInfo>
        <PositionName>
          {employee.positionName}
          {employee.isRepresentative === 'Y' && <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 600, color: '#fff', background: '#4AB3BC', borderRadius: '4px', padding: '1px 6px' }}>대표 관리자</span>}
          {employee.isDefaultAdmin === 'Y' && <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 600, color: '#fff', background: '#888', borderRadius: '4px', padding: '1px 6px' }}>기본 관리자</span>}
        </PositionName>
        {employee.username && <EmployeeName style={{ fontSize: '12px', color: '#999' }}>ID: {employee.username}</EmployeeName>}
        <EmployeeName>
          {employee.name} ({employee.phoneNumber})
        </EmployeeName>
      </EmployeeInfo>
      {isCurrentUser(employee) ? (
        <ActiveButton disabled>이용중</ActiveButton>
      ) : (
        <SelectButton
          onClick={() => handleSelectEmployee(employee)}>
          선택
        </SelectButton>
      )}
    </EmployeeCard>
  );

  return (
    <>
      <SwitchModal open={open} onClose={onClose}>
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>관리자 변경</ModalTitle>
            <CloseButton onClick={onClose}>
              <CloseIcon />
            </CloseButton>
          </ModalHeader>
          <ModalBody>
            {/* 대표/기본 관리자 */}
            <SectionDivider>
              <SectionLabel>대표/기본 관리자</SectionLabel>
            </SectionDivider>
            {!representativeAndDefaultList.some(e => e.isRepresentative === 'Y') && (
              <EmployeeCard style={{ justifyContent: 'center', padding: '20px', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <EmployeeName style={{ color: '#999', fontSize: '14px' }}>
                  권한 관리에서 <span style={{ color: '#555', fontWeight: 600, fontSize: '15px' }}>대표 관리자</span>를 설정해주세요.
                </EmployeeName>
                <span
                  onClick={() => { onClose(); navigate('/permission'); }}
                  style={{ color: '#4AB3BC', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  권한 관리로 이동 →
                </span>
              </EmployeeCard>
            )}
            {representativeAndDefaultList.map(renderEmployeeCard)}

            {/* 직원 목록 (직급별) */}
            {employeesByPosition.map(group => (
              <React.Fragment key={group.positionName}>
                <SectionDivider>
                  <SectionLabel>{group.positionName}</SectionLabel>
                </SectionDivider>
                {group.employees.map(renderEmployeeCard)}
              </React.Fragment>
            ))}
          </ModalBody>
        </ModalContainer>
      </SwitchModal>

      {selectedEmployee && (
        <>
          <PasswordInputModal
            open={passwordModalOpen}
            onClose={() => setPasswordModalOpen(false)}
            employee={selectedEmployee}
            token={managerToken}
            branchId={branchId}
            onSwitchSuccess={handleSwitchSuccess}
          />
          <SetPasswordModal
            open={setupPasswordModalOpen}
            onClose={() => setSetupPasswordModalOpen(false)}
            employee={selectedEmployee}
            token={managerToken}
            onSuccess={handleSetPasswordSuccess}
          />
        </>
      )}
    </>
  );
};

export { ORIGINAL_USER_KEY, EMPLOYEE_CACHE_KEY };
export default AdminSwitchModal;
